// A companion's settings: the owner's choices for it, kept apart from its design (what it is and
// what it can do). Plain data, so the preview keeps it in the browser and AresOS can keep it
// with its own settings:
//   { skin: 'gold', outfit: { head: 'crown', body: 'none' }, size: 1, activity: 'calm',
//     parachute: { color: 'match', pattern: 'stripes' },
//     does: { elytra: false, 'party.host': true } }       (only what differs from the defaults)
// size is a step bigger or smaller than the others (see SIZES in companion.js). activity is its own
// behavior level (OWN_LEVELS in activity.js: the screen's levels, then Still), or null: like
// everyone (the screen's level). Settings saved before it existed have none, so they stay like
// everyone.
// applySettings checks everything against what exists, so old or broken settings fall back to
// the defaults instead of breaking the companion.

import { lookFor, skinsFor } from './skins.js';
import { OUTFITS } from './outfits.js';
import { CHUTE_PATTERNS } from './props.js';
import { actionsFor, canByDefault } from './catalog.js';
import { SIZES, scaleOf, spotScale } from './companion.js';
import { OWN_LEVELS } from './activity.js';

export function settingsOf(me) {
  const head = 'restoreHead' in me ? me.restoreHead : me.outfit.head;     // not the nightcap it sleeps in
  return { skin: me.look.key, outfit: { ...me.outfit, head }, size: me.size || 0, activity: me.activity ?? null, parachute: { ...me.parachute }, does: { ...me.does } };
}

export function applySettings(me, s = {}) {
  const design = me.design, skins = skinsFor(design);
  me.look = lookFor(design, skins.includes(s.skin) ? s.skin : 'original');
  for (const b of me.babies) b.c.look = me.look;
  const outfit = { body: 'none', head: 'none' };
  for (const slot of design.outfits || []) if (OUTFITS[slot][s.outfit?.[slot]]) outfit[slot] = s.outfit[slot];
  if ('restoreHead' in me) { me.restoreHead = outfit.head; outfit.head = me.outfit.head; }   // asleep: the nightcap stays on
  me.outfit = outfit;
  me.size = String(s.size) in SIZES ? Number(s.size) : 0;
  me.s = me.anchor ? spotScale(me, me.anchor) : scaleOf(me);
  me.activity = Object.hasOwn(OWN_LEVELS, s.activity) ? s.activity : null;
  const color = s.parachute?.color, pattern = s.parachute?.pattern;
  me.parachute = { color: color === 'match' || skins.includes(color) ? color : 'match', pattern: CHUTE_PATTERNS[pattern] ? pattern : 'plain' };
  // Only choices that differ from the defaults are kept, so a later change of a default still reaches it.
  const known = new Set(actionsFor(design).map(a => a.id));
  me.does = Object.fromEntries(Object.entries(s.does || {})
    .filter(([id, on]) => known.has(id) && typeof on === 'boolean' && on !== canByDefault(design, id)));
}
